import { FormComponentProps } from 'antd/lib/form';
import { StylesProps, KeyLabel } from 'src/Library/Types';
import { OrgTypes } from 'src/Library/Constants';
import {
  newShipmentStyles,
  newShipmentThirdStyles,
  newShipmentSecondStyles,
  newShipmentFirstStyles,
} from './new-shipment.styles';
import { Execution } from 'src/Library/MasterDataType';
import GetExecutionData from 'src/dto/response/getExecution';
import { Ports } from 'src/components/common/Types/CommonTypes';
import { SEAddressType } from '../SE.interfaces';

interface ExecutionShipment extends GetExecutionData {
  destinationStuffingAddress: any;
  originStuffingAddress: any;
  stakeholder: any;
  customer: any;
  blNumberDisabled: boolean;
  blNumber: string;
  modeDisabled: boolean;
  incotermDisabled: boolean;
  carrierName: string;
  carrierDisabled: boolean;
  movementTypeDisabled: boolean;
  originPortDisabled: boolean;
  destinationPortDisabled: boolean;
  originCountryDisabled: boolean;
  destinationCountryDisabled: boolean;
  cargoDisabled: boolean;
  cargoProductDisable: boolean;
  stackableDisabled: boolean;
  containerDisabled: boolean;
  containerList: Container[];
  productList: Product[];
  productDisabled: boolean;
  originServicesDisabled: boolean;
  destinationServicesDisabled: boolean;
  pickupDisabled: boolean;
  stuffingDisabled: boolean;
  deStuffingDisabled: boolean;
  tncDisabled: boolean;
  stakeholderDisabled: boolean;
  originPortType: string;
  destinationPortType: string;
  teamIds: string;
  originPort: Ports;
  destinationPort: Ports;
  originCountry: KeyLabel;
  destinationCountry: KeyLabel;
  ctShipmentId: string;
  bookingDisabled: boolean;
  inquiryId: string;
  teamDetails: any;
  purchaseOrderNumberList?: string[];
  invoiceNumberList?: string[];
}

export type Product = {
  id: string;
  key?: string;
  unit?: string;
  type?: string;
  quantity?: number;
  internalName?: string;
  skuClientNumber?: string;
  cases?: string;
};

export type fieldConfig = {
  key: keyof Container,
  label: string,
  width?: string,
  required: boolean,
}

export type ContainerField = {
  label: string;
  func: (
    item: Container,
    {
      key,
      width,
      required,
    }: fieldConfig,
  ) => React.ReactElement;
  key?: string;
  width: string;
  required?: boolean;
}

export type Container = {
  id: string;
  type?: string;
  otherType?: string;
  size?: string;
  count?: number;
  totalWeight?: string;
  totalWeightUnit?: string;
  dimensionUnit?: string;
  length?: number,
  breadth?: number,
  height?: number,
  minVolumeUnit?: string;
  minVolume?: number;
  temperature?: string;
  grossWeight?: string;
  packageDimension?: {
    length: number;
    breadth: number;
    height: number;
    unit: string;
  };
  volume?: {
    value: number;
    unit: string;
  };
  weight?: {
    value: number;
    unit: string;
  };
};
interface CommonProps extends FormComponentProps {
  editData: ExecutionShipment;
  isParentOrg?: boolean;
  isChildOrg?: boolean;
  ctOrBooking?: string;
  executionMaster: Execution;
  handleClose: (success?: boolean) => void;
  disableBooking: boolean;
  productsList: Product[];
  containerList: Container[];
  customerDetails: any[];
  stakeholderDetails: any[];
  originAddressDetails: any[];
  destinationAddressDetails: any[];
  setProducts: any;
  setContainers: any;
  loading: boolean;
  inquiryMaster: any;
  domesticVehicleTypeSizeList: any[];
  formFieldConfig: any;
  isBlNumberPresent: boolean;
  isFieldDiabled: (disableKey?: string, isRequired?: boolean) => boolean;
  isConfigLoading: boolean;
  setCustomFieldsMap: ([]: any) => void;
  disableGrossWeightCalculation?: boolean;
  /** ISS-293251 org rollout switch for the detach-booking control (se_enable_booking_detach). */
  enableBookingDetach?: boolean;
  showRoadinsteadOfDomestic?: boolean;
  stakeholderAccountType?: string;
}

export interface NewShipmentProps extends CommonProps, StylesProps<typeof newShipmentStyles> {}
export interface NewShipmentThirdProps extends CommonProps, StylesProps<typeof newShipmentThirdStyles> {hide: boolean}
export interface NewShipmentSecondProps extends CommonProps, StylesProps<typeof newShipmentSecondStyles> {hide: boolean}
export interface NewShipmentFirstProps extends CommonProps, StylesProps<typeof newShipmentFirstStyles> {hide: boolean}

export enum CurrentPage {
  FIRST = 'FIRST',
  SECOND = 'SECOND',
  THIRD = 'THIRD',
};

export enum ButtonAction {
  PREV = 'PREV',
  NEXT = 'NEXT',
  SUBMIT = 'SUBMIT',
};

export interface NewShipmentState {
  currentPage: CurrentPage,
  visited: Record<CurrentPage, boolean>;
}

export type FormField = {
  key: string;
  required?: boolean;
  label: string;
  placeholder?: string;
  stakeholderType?: OrgTypes;
  initialValue?: any;
  countryKey?: string;
  addMoreLabel?: string;
  disableKey?: string;
  addressType?: SEAddressType;
  optionsList?: string[];
  configKey: string;
  step: CurrentPage;
  type?: string;
}

export interface ShipmentApiBody {
  id?: string;
  mode?: string;
  originAddress?: string;
  destinationAddress?: string;
  incoterm?: string;
  type?: string;
  cargoType?: string;
  cargoSubtype?: string;
  originServices?: string[];
  destinationServices?: string[];
  pickupAndDrop?: string;
  originLocationType?: string;
  originLocationId?: string;
  destinationLocationType?: string;
  destinationLocationId?: string;
  originPolCode?: string;
  destinationPodCode?: string;
  customFields?: Array<{ dbKey: string; value: any }>;
  teamIds?: string[];
  ffId?: string;
  chaId?: string;
  parentId?: string;
  childId?: string;
  transporterId?: string;
  cfsContractorId?: string;
  icdContractorId?: string;
  breakBulkId?: string;
  surveyorId?: string;
  interCartingId?: string;
  shipperId?: string;
  customerIds?: string[];
  customerId?: string;
  supplierIds?: string[];
  supplierId?: string;
  accessType?: string;
  originPortCode?: string;
  originPortType?: string;
  carrierCode?: string | null;
  destinationPortCode?: string;
  destinationPortType?: string;
  productDetails?: Product[];
  packagingDetails?: Container[];
  originCountryCode?: string;
  destinationCountryCode?: string;
  termsAndConditions?: string;
  containerNumber?: string;
  bookingNumber?: string;
  blNumber?: string;
  salesOrderNumber?: string;
  clientReferenceNumber?: string;
  invoiceNumberList?: string[];
  purchaseOrderNumberList?: string[];
  ctShipmentId?: string;
  documents?: any[];
  plantId?: string;
  // `null` is meaningful here: it is how an edit tells the backend to unlink the booking, as
  // distinct from `undefined`, which leaves the existing link untouched (ISS-293251).
  sourceObjectId?: string | null;
  sourceObjectType?: string | null;
  watcherList?: any[];
  isCargoStackable?: boolean;
}
